import * as React from "react";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { DistributionDividendPreference } from "../../../../../../../../Wrap/Constants/Enums/Domain/DistributionDividendPreference";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { MoneyOrPercentageType } from "../../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { InstrumentSelection } from "../../../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelection";
import { InstrumentSelections } from "../../../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelections";
import { ApplyModel } from "../../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { User } from "../../../../../../../../Wrap/Models/Models/User/User";
import { HierarchyIdHelper } from "../../../../../../../Shared/Common/HierarchyIdHelper";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import {
    getISTPaymentAmount,
    initPensionInstrumentSelections,
    initWeighting,
} from "../../../../../../../Shared/IST/Components/Shared/Utils";
import { InvestmentSelectionTool as IST } from "../../../../../../../Shared/IST/InvestmentSelectionTool";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import { AllocationTableItem } from "../../../../../../../Shared/IST/Store/SelectedInvesmentStoreStatic";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../../Shared/Process/Process/WizardLinter";
import { Action } from "../../../../../../Actions/Action";
import { TreasuredAssetsStore, UserStore } from "../../../../../../Stroes/Variable";
import { BankAccountOwnerPaymentTypeMap, PaymentType } from "../../../../Common/Resource/Contents";
import { RestrictionStore } from "../../../../Common/Restrictions/Variable";
import { EWizardLinterHelper } from "../../../Resource/EWizardLinterHelper";
import { AdviserContextStoreStatic } from "../../../Stores/Variable";

interface IInvestmentSelectionToolProps extends IProcessComponentProps {
    data: ApplyModel;
    treasuredAssetProductIds: number[];
    onTreasuredAssetsStoreChange: () => void;
    onChangeTreasuredAssets: (hierarchyId, productId, value) => void;
}

interface IInvestmentSelectionToolState {
    userInfo: User;
}

export class InvestmentSelectionTool extends ProcessComponent<
    IInvestmentSelectionToolProps,
    IInvestmentSelectionToolState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
        };

        this.subscribeToStore(TreasuredAssetsStore, () => this.props.onTreasuredAssetsStoreChange());
    }

    componentDidMount() {
        super.componentDidMount();

        const subAccountInstructionId = HierarchyIdHelper.generateHierarchyId(this.props.data.PensionApply.HierarchyId)
            .entityId;

        Action.getInstructionTreasuredAssetsProductIds(subAccountInstructionId);
    }

    private onInvestmentChanges = (changes) => {
        const preRetirement: AllocationTableItem[] = changes.Instruments.map((ele) => ({
            Instrument: ele.Instrument,
            Single: ele.Single,
            Regular: ele.Regular,
            Transfer: ele.Transfer,
            isToogle: ele.isToogle,
            needToggle: ele.needToggle,
            isTargetAllocation: ele.isTargetAllocation,
            underlying: ele.underlying,
            modelPortfolioType: ele.modelPortfolioType,
            ClientRegular: ele.ClientRegular,
            EmployerRegular: ele.EmployerRegular,
            ThirdPartyRegular: ele.ThirdPartyRegular,
            EmployerSalaryDeductionRegular: ele.EmployerSalaryDeductionRegular

        }));
        const postRetirement = changes.Instruments.map((ele) => ({
            Instrument: ele.Instrument,
            Transfer: ele.PostTransfer || null,
            Single: initWeighting(),
            Regular: initWeighting(),
        }));
        this.onDataChange(
            this.props.onChange,
            {
                PreRetirementInstrumentSelections: this.initInstrumentSelection(preRetirement),
                PostRetirementInstrumentSelections: this.initInstrumentSelection(postRetirement),
            },
            this.props.data,
            "PensionApply"
        );
    };

    private initInstrumentSelection = (instruments: InstrumentSelection[]): InstrumentSelections => {
        return {
            Type: MoneyOrPercentageType.Percentage,
            Instruments: instruments,
            DistributionDividendPreference: DistributionDividendPreference.NotSpecified,
        };
    };

    public render(): JSX.Element {
        const { onChange, data, onChangeTreasuredAssets, treasuredAssetProductIds, orderType, onOrderTypeChange } = this.props;
        const paymentAmount = getISTPaymentAmount(data?.PensionApply, "PensionTransfers");
        const accountsFromExisting = data.AllBankAccounts || [];
        const bankAccountInstructionsFiltered =
            (data.BankAccountInstructions || []).filter(
                (x) => BankAccountOwnerPaymentTypeMap[x.Owner].indexOf(PaymentType.Client) != -1
            ) || [];

        const bankAccounts = [...accountsFromExisting, ...bankAccountInstructionsFiltered];
        const modelData = JSON.parse(JSON.stringify(data?.PensionApply));
        modelData.CashInstrument = data.CashInstrument;
        modelData.OwnerHierarchyId = data.OwnerHierarchyId;

        const preRetirement = data?.PensionApply?.PreRetirementInstrumentSelections?.Instruments || [];
        const postRetirement = data?.PensionApply?.PostRetirementInstrumentSelections?.Instruments || [];
        const instrumentSelection = this.initInstrumentSelection([]);
        instrumentSelection.Instruments = initPensionInstrumentSelections(preRetirement, postRetirement);
        const adviser = AdviserContextStoreStatic.getData();

        let restrictedMessage = "An account with suspended or restricted activities cannot setup Investment selection";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <WizardLinter
                status={EWizardLinterStatus.PleaseCheck}
                enabled={EWizardLinterHelper.shouldDisplay(SubAccountType.JuniorSipp)}
            >
                <h2 className="mb-20">Investment selection</h2>
                {RestrictionStore.buyDisabled() && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                <IST
                    withReRegistrationTransferIn={false}
                    data={instrumentSelection}
                    modelData={modelData}
                    onRef={(ref) => this.appendChild(ref)}
                    onChange={(changes) => {
                        if (changes.orderType) {
                            onOrderTypeChange(changes.orderType);
                        } else if (changes.AdviceGivenForOffPlatform != undefined) {
                            this.onDataChange(
                                (changes) => this.onDataChange(onChange, changes, data, "PensionApply"),
                                changes,
                                data.PensionApply,
                                "InstrumentSelections"
                            );
                        } else {
                            this.onInvestmentChanges(changes)
                        }
                    }}
                    showErrorMsgPopUp={(msg) => this.props.showErrorMsgPopUp(msg)}
                    type={ProcessType.NB}
                    hiercharyId={data?.PensionApply?.HierarchyId}
                    headAccountHierarchyId={data.ExistingAccountHierarchyId}
                    companyCode={adviser?.Company}
                    claccountId={null}
                    product={SubAccountType.JuniorSipp}
                    paymentAmount={{ Amount: paymentAmount, Currency: CurrencyCode.GBP }}
                    bankAccounts={bankAccounts}
                    onBankChange={(bankInfo) => {
                        const banks = data.BankAccountInstructions.concat(bankInfo);
                        onChange({ BankAccountInstructions: banks });
                    }}
                    onDistributionDividendOptions={(changes) => {
                        this.onDataChange(
                            (changes) => this.onDataChange(onChange, changes, data, "PensionApply"),
                            changes,
                            data.PensionApply,
                            "DistributionDividendOptions"
                        );
                    }}
                    decisionMakerHierarchyId={data?.PensionApply?.HierarchyId}
                    accountAdviceType={data?.AccountAdviceType}
                    accountServiceProposition={data?.AccountServiceProposition}
                    treasuredAssetsProductIds={treasuredAssetProductIds}
                    onChangeTreasuredAssets={(productId, value) => {
                        onChangeTreasuredAssets(data.PensionApply.HierarchyId, productId, value);
                    }}
                    orderType={orderType}
                />
            </WizardLinter>
        );
    }
}
